import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Pagination,
  PaginationItem,
  MenuItem,
  Select,
  FormControl,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import FilterManager from "./FilterComponents/FilterManager";
import TableHeader from "./TableHeader";
import TableSkeleton from "./TableSkeleton";

const ReusableTable = ({
  columns,
  data,
  renderCell,
  renderHeaderCell = null,
  renderRowHeader = null,
  renderRowFooter = null,
  getRowSx = null,
  getCellSx = null,
  getRowProps = null,
  rowsPerPageOptions = [10, 20, 40, 80],
  rowsPerPage: initialRowsPerPage = null,
  height = "auto",
  showPagination = true,
  onFilterChange = null,
  filters = {},
  loading = false,
  emptyState = null,
  sx = {},
  enableHorizontalScroll = false,
  headerRightPadding = 0,
  // sortBy: array e.g. ['-readiness'] (desc) or ['readiness'] (asc), [] = no sort. When user clicks, onSortChange(newSortBy) is called.
  sortBy = [],
  onSortChange = null,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(
    initialRowsPerPage || rowsPerPageOptions[0]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  // Initialize filter manager with filters
  const filterManager = FilterManager({
    onFilterChange: onFilterChange,
    filters: filters,
  });

  // Calculate paginated data based on current page and rows per page
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return data && data.slice(startIndex, startIndex + rowsPerPage);
  }, [page, rowsPerPage, data]);

  // Reset page when data changes
  React.useEffect(() => {
    setPage(1);
  }, [data]);

  // Refs for scroll sync
  const headerRef = React.useRef(null);
  const bodyRef = React.useRef(null);

  // Bidirectional scroll sync between header and body
  React.useEffect(() => {
    const body = bodyRef.current;
    const header = headerRef.current;
    if (!body || !header) return;

    // Prevent infinite loop flag
    let isSyncing = false;

    // Sync body scroll to header
    const syncBodyToHeader = () => {
      if (!isSyncing) {
        isSyncing = true;
        header.scrollLeft = body.scrollLeft;
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      }
    };

    // Sync header scroll to body
    const syncHeaderToBody = () => {
      if (!isSyncing) {
        isSyncing = true;
        body.scrollLeft = header.scrollLeft;
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      }
    };

    body.addEventListener("scroll", syncBodyToHeader);
    header.addEventListener("scroll", syncHeaderToBody);

    return () => {
      body.removeEventListener("scroll", syncBodyToHeader);
      header.removeEventListener("scroll", syncHeaderToBody);
    };
  }, [loading]);

  const tableStyle = enableHorizontalScroll
    ? {
      tableLayout: "auto",
      width: "max-content",
      minWidth: "100%",
    }
    : {
      tableLayout: "fixed !important",
      width: "100%",
      minWidth: "1024px",
    };

  return (
    <>
      <TableContainer
        sx={{
          height: height,
          border: "1px solid #E0E0E0",
          boxShadow: "none",
          overflow: "hidden", // Changed from "auto" to prevent scrollbar on container
          width: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <TableSkeleton columns={columns} filterManager={filterManager} />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              ref={headerRef}
              sx={{
                flexShrink: 0,
                overflowX: "auto",
                overflowY: "hidden",
                boxSizing: "border-box",
                backgroundColor: "#F7F7FC",
                position: "relative",
                zIndex: 2,
                scrollbarGutter: "stable", // Match body scrollbar space
                pr: headerRightPadding,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              <Table
                sx={{
                  ...tableStyle,
                }}
              >
                <colgroup>
                  {columns.map((column, index) => (
                    <col
                      key={index}
                      style={{
                        width:
                          column.width ||
                          (column.minWidth ? `${column.minWidth}px` : "auto"),
                        minWidth: column.minWidth
                          ? `${column.minWidth}px`
                          : column.width || "auto",
                      }}
                    />
                  ))}
                </colgroup>
                <TableHeader
                  columns={columns}
                  filterManager={filterManager}
                  renderHeaderCell={renderHeaderCell}
                  sortBy={sortBy}
                  onSortChange={onSortChange}
                />
              </Table>
            </Box>
            {/* Body - scrollable with scrollbar */}
            <Box
              ref={bodyRef}
              sx={{
                flex: 1,
                overflow: "auto",
                minHeight: 0, // Ensure flex child can shrink below content size
                scrollbarWidth: "thin",
                scrollbarColor: "#c1c1c1 #f0f0f0",
                scrollbarGutter: "stable", // Always reserve space for scrollbar
                "&::-webkit-scrollbar": {
                  width: "17px",
                  display: "block",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f0f0f0",
                  visibility: "visible",
                  display: "block",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "4px",
                  minHeight: "20px",
                  opacity: 0.3,
                },
                "&:hover::-webkit-scrollbar-thumb": {
                  opacity: 1,
                },
              }}
            >
              <Table
                sx={{
                  ...tableStyle,
                  ...sx,
                }}
              >
                <colgroup>
                  {columns.map((column, index) => (
                    <col
                      key={index}
                      style={{
                        width: column.width || "auto",
                        minWidth: column.width || "auto",
                        maxWidth: column.width || "auto",
                      }}
                    />
                  ))}
                </colgroup>
                <TableBody>
                  {data?.length > 0 ? (
                    (showPagination ? paginatedData : data).map(
                      (row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          {/* Row header - content above the main row */}
                          {renderRowHeader && (
                            <TableRow sx={{ borderBottom: "none" }}>
                              <TableCell
                                colSpan={columns.length}
                                sx={{ padding: "0px", borderBottom: "none" }}
                              >
                                {renderRowHeader(row, rowIndex)}
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Main row content */}
                          <TableRow
                            {...(getRowProps ? getRowProps(row, rowIndex) : {})}
                            sx={{
                              borderTop: renderRowHeader
                                ? "none"
                                : "1px solid #e0e0e0",

                              ...(getRowSx ? getRowSx(row, rowIndex) : {}),
                              ...(getRowProps && getRowProps(row, rowIndex)?.sx
                                ? getRowProps(row, rowIndex).sx
                                : {}),
                            }}
                          >
                            {columns.map((column, colIndex) => (
                              <TableCell
                                key={colIndex}
                                align={column.align || "left"}
                                sx={{
                                  padding: "8px",
                                  width: column.width
                                    ? `${column.width} !important`
                                    : "auto",
                                  minWidth: column.width
                                    ? `${column.width} !important`
                                    : "auto",
                                  maxWidth: column.width
                                    ? `${column.width} !important`
                                    : "auto",
                                  textAlign: column.align || "left",
                                  borderTop: renderRowHeader
                                    ? "none"
                                    : "1px solid #e0e0e0",
                                  borderBottom: renderRowFooter
                                    ? "none"
                                    : "1px solid #e0e0e0",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  ...(getCellSx
                                    ? getCellSx(row, column, rowIndex, colIndex)
                                    : {}),
                                }}
                              >
                                {renderCell
                                  ? renderCell(row, column)
                                  : row[column.field]}
                              </TableCell>
                            ))}
                          </TableRow>

                          {/* Row footer - content below the main row */}
                          {renderRowFooter && (
                            <TableRow sx={{ borderTop: "none" }}>
                              <TableCell
                                colSpan={columns.length}
                                sx={{ padding: "0px", borderTop: "none" }}
                              >
                                {renderRowFooter(row, rowIndex)}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{
                          height: "100%",
                          textAlign: "center",
                          padding: "40px",
                          color: "text.secondary",
                          verticalAlign: "middle",
                          border: "none",
                        }}
                      >
                        {emptyState || (
                          <Typography variant="body1">
                            No data available!
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </TableContainer>
      {showPagination && !loading && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ padding: 2 }}
        >
          {/* Rows per page selection */}
          <FormControl variant="outlined" size="small">
            {/* <InputLabel>Rows per page</InputLabel> */}
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              MenuProps={{
                container: document.body,
                style: {
                  zIndex: 9999,
                },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Pagination controls */}
          <Pagination
            count={Math.ceil(data?.length / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            variant="outlined"
            shape="rounded"
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  background: item.selected ? "#031B89 !important" : "",
                  color: item.selected ? "#fff !important" : "",
                }}
              />
            )}
          />
        </Stack>
      )}
    </>
  );
};

export default ReusableTable;
